import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import {
  InvitationCreateDto,
  InvitationDto,
  InvitationService
} from '../services/invitation.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';

@Component({
  selector: 'app-invitations-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--violet">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16v16H4z"/>
                <path d="m22 6-10 7L2 6"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Invitations</h1>
          <p class="page-sub">Create and manage cross-organization invitations for onboarding.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          <div class="card">
            <div class="card-hd"><span class="card-title">Create Invitation</span></div>
            <div class="form-grid">
              <div class="field">
                <label class="field-label" for="inv-org">Organization</label>
                <select id="inv-org" class="field-input field-select" [(ngModel)]="draft.organizationId">
                  <option [ngValue]="0">Select organization…</option>
                  @for (org of organizations(); track org.id) {
                    <option [ngValue]="org.id">{{ org.displayName || org.legalName }}</option>
                  }
                </select>
              </div>

              <div class="field">
                <label class="field-label" for="inv-email">Email</label>
                <input id="inv-email" class="field-input" type="email" placeholder="name@organization.com" [(ngModel)]="draft.email" />
              </div>

              <div class="field">
                <label class="field-label" for="inv-role">Role</label>
                <select id="inv-role" class="field-input field-select" [(ngModel)]="draft.role">
                  @for (role of roleOptions; track role) {
                    <option [value]="role">{{ role }}</option>
                  }
                </select>
              </div>

              <div class="field">
                <label class="field-label" for="inv-expiration-days">Expiration Days</label>
                <input id="inv-expiration-days" class="field-input" type="number" min="1" max="30" [(ngModel)]="draft.expirationDays" />
                <small class="field-help" title="Invitation expires this many days after creation.">
                  Default is 7 days. Valid range is 1–30 days.
                </small>
              </div>
            </div>

            <div class="action-bar">
              <button class="btn btn--primary" (click)="createInvitation()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
                Create Invitation
              </button>
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">All Invitations</span>
              <div class="card-tools">
                <div class="tool-input-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input class="tool-input" placeholder="Filter invitations…" [(ngModel)]="filterText" />
                </div>

                <select class="tool-select" [(ngModel)]="sortMode">
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="email-asc">Email A-Z</option>
                  <option value="email-desc">Email Z-A</option>
                  <option value="status">Status</option>
                </select>

                <span class="card-count">{{ filteredInvitations.length }} invitation{{ filteredInvitations.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Email</span>
                <span>Organization</span>
                <span>Role</span>
                <span>Status</span>
                <span>Expires</span>
                <span>Actions</span>
              </div>

              @for (inv of filteredInvitations; track inv.id) {
                <div class="table-row">
                  <div>
                    <div class="cell-main">{{ inv.email }}</div>
                    <div class="token">Token: {{ inv.token }}</div>
                  </div>
                  <span>{{ getOrganizationDisplay(inv.organizationId) }}</span>
                  <span>{{ inv.role }}</span>
                  <span>{{ inv.status }}</span>
                  <span>{{ formatDateTime(inv.expiresAt) }}</span>
                  <div class="row-actions">
                    <button class="btn btn--ghost" (click)="resend(inv.id)">Resend</button>
                    <button class="btn btn--danger" (click)="remove(inv.id)">Delete</button>
                    <button class="btn btn--ghost" (click)="generateInvitationHtml(inv)">Generate HTML</button>
                  </div>
                </div>
              } @empty {
                <div class="empty">No invitations found.</div>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page {
      min-height: 100vh;
    }

    .page-hero {
      background:
        radial-gradient(ellipse at 12% 60%, color-mix(in srgb, #8b5cf6 8%, transparent) 0%, transparent 55%),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner,
    .page-body-inner {
      max-width: 1100px;
      margin: 0 auto;
    }

    .home-btn {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      color: var(--color-muted);
      text-decoration: none;
      font-size: .9rem;
      margin-bottom: .95rem;
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: .95rem;
      display: grid;
      place-items: center;
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, #8b5cf6 45%, #0000);
      color: #c4b5fd;
      background: color-mix(in srgb, #8b5cf6 18%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, #8b5cf6 24%, transparent);
    }

    .page-title {
      margin: 0;
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      letter-spacing: -.01em;
    }

    .page-sub {
      margin: .5rem 0 0;
      color: var(--color-muted);
      max-width: 60ch;
    }

    .page-body {
      padding: 1.5rem;
    }

    .status-banner {
      margin-bottom: .9rem;
      border: 1px solid color-mix(in srgb, var(--color-accent-soft) 45%, transparent);
      background: color-mix(in srgb, var(--color-accent-soft) 10%, transparent);
      color: var(--color-text);
      border-radius: .75rem;
      padding: .7rem .9rem;
      font-size: .92rem;
    }

    .card {
      border: 1px solid var(--color-border);
      border-radius: .95rem;
      overflow: hidden;
      margin-bottom: 1rem;
      background: color-mix(in srgb, var(--color-panel) 80%, transparent);
    }

    .card-hd {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--color-border);
      padding: .85rem 1rem;
      gap: .6rem;
      flex-wrap: wrap;
    }

    .card-title {
      font-weight: 600;
    }

    .card-count {
      color: var(--color-muted);
      font-size: .9rem;
    }

    .card-tools {
      display: flex;
      align-items: center;
      gap: .55rem;
      flex-wrap: wrap;
    }

    .tool-input-wrap {
      display: inline-flex;
      align-items: center;
      gap: .35rem;
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      padding: .4rem .55rem;
    }

    .tool-input {
      border: 0;
      background: transparent;
      color: var(--color-text);
      min-width: 210px;
      outline: none;
    }

    .tool-select {
      border: 1px solid color-mix(in srgb, var(--color-border) 85%, #0f172a);
      border-radius: .4rem;
      background: #0f172a;
      color: #f8fafc;
      color-scheme: dark;
      cursor: pointer;
      font-size: 0.82rem;
      font-weight: 500;
      padding: .4rem .55rem;
    }

    .tool-select:hover { border-color: #22d3ee; }
    .tool-select:focus { border-color: #22d3ee; outline: none; }
    .tool-select option { background: #0f172a; color: #f8fafc; }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: .8rem;
      padding: 1rem;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: .35rem;
    }

    .field-label {
      font-size: .85rem;
      color: var(--color-muted);
    }

    .field-input {
      padding: .58rem .68rem;
      border: 1px solid var(--color-border);
      border-radius: .55rem;
      background: transparent;
      color: var(--color-text);
    }

    .field-select {
      appearance: none;
      background: #0f172a;
      border: 1px solid color-mix(in srgb, var(--color-border) 85%, #0f172a);
      border-radius: .4rem;
      color: #f8fafc;
      color-scheme: dark;
      cursor: pointer;
      font-size: 0.82rem;
      font-weight: 500;
      transition: border-color 0.15s;
    }

    .field-select:hover { border-color: #22d3ee; }
    .field-select:focus { border-color: #22d3ee; outline: none; }
    .field-select option { background: #0f172a; color: #f8fafc; }

    .field-help {
      font-size: .78rem;
      color: var(--color-muted);
    }

    .action-bar {
      display: flex;
      gap: .6rem;
      padding: 0 1rem 1rem;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: .4rem;
      border-radius: .6rem;
      border: 1px solid var(--color-border);
      background: transparent;
      color: var(--color-text);
      padding: .5rem .8rem;
      cursor: pointer;
    }

    .btn--primary {
      background: color-mix(in srgb, var(--color-accent-soft) 28%, transparent);
      border-color: color-mix(in srgb, var(--color-accent-soft) 60%, var(--color-border));
      color: #fff;
    }

    .btn--ghost {
      background: transparent;
    }

    .btn--danger {
      border-color: color-mix(in srgb, #ef4444 55%, var(--color-border));
      color: #fca5a5;
    }

    .table-wrap {
      display: flex;
      flex-direction: column;
    }

    .table-head,
    .table-row {
      display: grid;
      grid-template-columns: 2.2fr 1fr 1fr 1fr 1.2fr 2fr;
      gap: .7rem;
      align-items: center;
      padding: .7rem 1rem;
    }

    .table-head {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      font-size: .8rem;
      letter-spacing: .02em;
      text-transform: uppercase;
      font-weight: 600;
    }

    .table-row {
      border-bottom: 1px solid var(--color-border);
    }

    .cell-main {
      font-weight: 500;
    }

    .token {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: .72rem;
      margin-top: .22rem;
      color: var(--color-muted);
      word-break: break-all;
    }

    .row-actions {
      display: flex;
      gap: .4rem;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .empty {
      padding: 1rem;
      color: var(--color-muted);
    }

    @media (max-width: 980px) {
      .form-grid {
        grid-template-columns: 1fr;
      }

      .table-head {
        display: none;
      }

      .table-row {
        grid-template-columns: 1fr;
        gap: .45rem;
      }

      .row-actions {
        justify-content: flex-start;
      }
    }
  `]
})
export class InvitationsPageComponent implements OnInit {
  private readonly invitationService = inject(InvitationService);
  private readonly organizationService = inject(OrganizationService);

  readonly invitations = signal<InvitationDto[]>([]);
  readonly organizations = signal<OrganizationDto[]>([]);
  readonly message = signal('');
  readonly roleOptions = ['TeamMember', 'EventPlanner', 'OrgAdmin', 'SiteAdmin'];

  filterText = '';
  sortMode: 'newest' | 'oldest' | 'email-asc' | 'email-desc' | 'status' = 'newest';

  draft: InvitationCreateDto = {
    organizationId: 0,
    email: '',
    role: 'TeamMember',
    expirationDays: 7
  };

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadInvitations();
  }

  get filteredInvitations(): InvitationDto[] {
    const term = this.filterText.trim().toLowerCase();

    const filtered = this.invitations().filter(inv => {
      if (!term) {
        return true;
      }

      const orgName = this.getOrganizationDisplay(inv.organizationId).toLowerCase();
      return inv.email.toLowerCase().includes(term)
        || inv.role.toLowerCase().includes(term)
        || inv.status.toLowerCase().includes(term)
        || orgName.includes(term);
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (this.sortMode === 'email-asc') {
        return a.email.localeCompare(b.email);
      }

      if (this.sortMode === 'email-desc') {
        return b.email.localeCompare(a.email);
      }

      if (this.sortMode === 'status') {
        return a.status.localeCompare(b.status) || a.email.localeCompare(b.email);
      }

      const aTime = new Date(a.dateCreated).getTime();
      const bTime = new Date(b.dateCreated).getTime();
      if (this.sortMode === 'oldest') {
        return aTime - bTime;
      }

      return bTime - aTime;
    });

    return sorted;
  }

  loadOrganizations(): void {
    this.organizationService.getAll().subscribe({
      next: data => this.organizations.set(data),
      error: () => this.message.set('Failed to load organizations (SiteAdmin required).')
    });
  }

  loadInvitations(): void {
    this.invitationService.getAll().subscribe({
      next: data => this.invitations.set(data),
      error: () => this.message.set('Failed to load invitations (SiteAdmin required).')
    });
  }

  createInvitation(): void {
    if (!this.draft.organizationId || !this.draft.email) {
      this.message.set('Organization and email are required.');
      return;
    }

    if (!this.isValidEmail(this.draft.email)) {
      this.message.set('Enter a valid email address.');
      return;
    }

    this.draft.expirationDays = this.normalizeExpirationDays(this.draft.expirationDays);

    this.invitationService.create(this.draft).subscribe({
      next: created => {
        this.message.set(`Invitation created. Token: ${created.token}`);
        this.draft.email = '';
        this.draft.expirationDays = 7;
        this.loadInvitations();
      },
      error: () => this.message.set('Failed to create invitation.')
    });
  }

  resend(id: number): void {
    this.invitationService.resend(id).subscribe({
      next: () => {
        this.message.set('Invitation resent with new token.');
        this.loadInvitations();
      },
      error: () => this.message.set('Failed to resend invitation.')
    });
  }

  remove(id: number): void {
    this.invitationService.delete(id).subscribe({
      next: () => {
        this.message.set('Invitation deleted.');
        this.loadInvitations();
      },
      error: () => this.message.set('Failed to delete invitation.')
    });
  }

  generateInvitationHtml(inv: InvitationDto): void {
    const baseUrl = window.location.origin;
    const org = this.organizations().find(o => o.id === inv.organizationId);
    const orgName = org?.displayName || org?.legalName || `Organization #${inv.organizationId}`;
    const invitationLink = `${baseUrl}/accept-invitation?token=${encodeURIComponent(inv.token)}`;
    const htmlContent = `<!DOCTYPE html>
<html><body style="font-family:sans-serif; color:#222;">
  <h2>You're Invited to Join ${orgName}</h2>
  <p>Hello,</p>
  <p>You have been invited to join <b>${orgName}</b> as an <b>${inv.role}</b> on United Team Building.</p>
  <p>To get started, simply click the link below:</p>
  <p style="margin:1.5em 0;"><a href="${invitationLink}" style="background:#8b5cf6;color:#fff;padding:0.7em 1.2em;border-radius:6px;text-decoration:none;font-weight:bold;">Accept Invitation</a></p>
  <p>If the button above does not work, copy and paste this link into your browser:</p>
  <p style="font-family:monospace;word-break:break-all;">${invitationLink}</p>
  <p><b>Note:</b> This invitation will expire on <b>${inv.expiresAt}</b>.</p>
  <p>If you did not expect this invitation, you can ignore this email.</p>
</body></html>`;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invitation-${inv.id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  formatDateTime(value: string): string {
    if (!value) {
      return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString();
  }

  getOrganizationDisplay(organizationId: number): string {
    const org = this.organizations().find(o => o.id === organizationId);
    if (!org) {
      return `Org ${organizationId}`;
    }

    return org.displayName || org.legalName || `Org ${organizationId}`;
  }

  private normalizeExpirationDays(value: number | undefined): number {
    const fallback = 7;
    const numericValue = typeof value === 'number' ? value : Number.NaN;
    if (!Number.isFinite(numericValue)) {
      return fallback;
    }

    return Math.min(30, Math.max(1, Math.floor(numericValue)));
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }
}
